import { styled, type Theme } from "@material-ui/core";
import { PatientType } from "elephant-web/gql";
import type { ComponentProps } from "react";
import { useController } from "react-hook-form";
import { InvoiceCardSection } from "../invoice-card-shell";
import { INVOICE_CARD_COLORS, INVOICE_CARD_RADIUS } from "./tokens";

const SEGMENT_RADIUS = 6;

const Row = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const Segments = styled("div")({
  display: "inline-flex",
  gap: 3,
  padding: 3,
  backgroundColor: INVOICE_CARD_COLORS.pageTrack,
  borderRadius: INVOICE_CARD_RADIUS.inner,
});

const Segment = styled("button")<Theme, { "data-active": boolean }>(
  ({ theme, ...props }) => ({
    border: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 600,
    fontSize: 13,
    padding: "7px 18px",
    borderRadius: SEGMENT_RADIUS,
    backgroundColor: props["data-active"]
      ? theme.palette.primary.main
      : "transparent",
    color: props["data-active"]
      ? theme.palette.primary.contrastText
      : theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: props["data-active"]
        ? theme.palette.primary.main
        : INVOICE_CARD_COLORS.pageTrack,
    },
  }),
);

const LineCount = styled("div")<Theme>(({ theme }) => ({
  marginLeft: "auto",
  fontWeight: 500,
  fontSize: 13,
  color: theme.palette.text.secondary,
}));

/**
 * The encounter type drives whether the stay-details band renders at all, so it
 * registers itself against the draft's form rather than holding local state.
 */
export const EncounterTypeRow: FC<
  {
    fieldNames: { encounterType: string };
    lineItemCount: number;
  } & Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({ fieldNames, lineItemCount, ...restProps }) => {
  const { field } = useController({ name: fieldNames.encounterType });

  return (
    <InvoiceCardSection paddingTop={12} paddingBottom={12} {...restProps}>
      <Row>
        <Segments>
          {[PatientType.Outpatient, PatientType.Inpatient].map((type) => (
            <Segment
              key={type}
              type="button"
              data-active={field.value === type}
              aria-pressed={field.value === type}
              onClick={() => field.onChange(type)}
            >
              {type}
            </Segment>
          ))}
        </Segments>
        <LineCount>
          {lineItemCount} {lineItemCount === 1 ? "line item" : "line items"}
        </LineCount>
      </Row>
    </InvoiceCardSection>
  );
};
